import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { IconButton, Drawer } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import { useAuth } from './LaunchGate'
import { useTheme } from '../contexts/ThemeContext'
import { colors } from '../styles/theme'

const publicNav = [
  { name: 'Home', href: '/' },
  { name: 'Episodes', href: '/episodes' },
  { name: 'Blog', href: '/blog' },
  { name: 'Guests', href: '/guests' },
  { name: 'About', href: '/about' },
  { name: 'Subscribe', href: '/subscribe' },
  { name: 'Contact', href: '/contact' },
]

const contributorNav = [
  { name: 'Prompt Ideas', href: '/prompt-ideas' },
  { name: 'Admin', href: '/admin' },
]

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()
  const { isContributor } = useAuth()
  const { toggleTheme, isDark } = useTheme()
  const navigation = isContributor ? [...publicNav, ...contributorNav] : publicNav

  const isActive = (path) => {
    return location.pathname === path
  }

  return (
    <header className="sticky top-0 z-40 bg-charcoal/95 backdrop-blur-sm border-b border-slate">
      <nav className="container-custom py-4" aria-label="Main navigation">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <motion.div
              className="flex items-center space-x-2"
              whileHover={{ scale: 1.05 }}
              transition={{ type: 'spring', stiffness: 400, damping: 10 }}
            >
              {/* Thought bubble + terminal icon */}
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan to-purple rounded-full flex items-center justify-center">
                  <span className="text-charcoal font-mono font-bold text-lg">&gt;_</span>
                </div>
                {/* Small bubble */}
                <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-cyan rounded-full"></div>
              </div>
              <div>
                <h1 className="text-xl font-bold text-off-white">Prompted Thoughts</h1>
                <p className="text-xs text-gray-400 font-mono hidden sm:block">
                  AI • Apps • DX • Data
                </p>
              </div>
            </motion.div>
          </Link>

          {/* Desktop Navigation + Theme Toggle */}
          <div className="hidden md:flex items-center space-x-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive(item.href)
                    ? 'bg-cyan/10 text-cyan'
                    : 'text-gray-300 hover:text-off-white hover:bg-slate'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <IconButton
              onClick={toggleTheme}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              sx={{
                color: '#e6edf3',
                ml: 1,
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'rotate(30deg)', color: '#00ff9f' },
              }}
            >
              {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </div>

          {/* Mobile: theme toggle + menu button */}
          <div className="flex items-center gap-1 md:hidden">
            <IconButton
              onClick={toggleTheme}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              sx={{ color: colors.textPrimary }}
              size="small"
            >
              {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
            <IconButton
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
              sx={{ color: colors.textPrimary }}
            >
              <MenuIcon />
            </IconButton>
          </div>
        </div>
      </nav>

      {/* Mobile menu drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: '80%',
            maxWidth: '320px',
            backgroundColor: colors.bgPage,
            color: colors.textPrimary,
            borderLeft: `1px solid ${colors.borderAccent}`,
          },
        }}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-lg font-bold">Menu</h2>
            <IconButton
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
              sx={{ color: colors.textPrimary }}
            >
              <CloseIcon />
            </IconButton>
          </div>

          <nav className="space-y-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg text-base font-medium transition-all ${
                  isActive(item.href)
                    ? 'bg-cyan/10 text-cyan'
                    : 'text-gray-300 hover:bg-slate hover:text-off-white'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </Drawer>
    </header>
  )
}

export default Header
